export interface ProblemDetails {
  readonly type: string
  readonly title: string
  readonly status: number
  readonly detail?: string | undefined
  readonly instance?: string | undefined
  readonly [extension: string]: unknown
}

export interface ValidationIssue {
  readonly pointer: string
  readonly message: string
  readonly code: string
}

export interface FailureContext {
  readonly status: number
  readonly problem: ProblemDetails | null
  readonly requestId: string | null
  readonly retryable: boolean
  readonly message: string
  readonly detail?: unknown
}

export type ApiFailure =
  | (FailureContext & { readonly kind: 'Unauthenticated' })
  | (FailureContext & { readonly kind: 'Forbidden' })
  | (FailureContext & { readonly kind: 'NotFound' })
  | (FailureContext & {
      readonly kind: 'Conflict'
      readonly code: string | null
      readonly currentRevision: number | null
      readonly sections: readonly string[]
    })
  | (FailureContext & { readonly kind: 'StaleWrite' })
  | (FailureContext & {
      readonly kind: 'Validation'
      readonly issues: readonly ValidationIssue[]
    })
  | (FailureContext & { readonly kind: 'Network' })
  | (FailureContext & { readonly kind: 'Unexpected' })

export type ApiFailureKind = ApiFailure['kind']
export type StaleWriteFailure = Extract<ApiFailure, { kind: 'StaleWrite' }>
export type ValidationFailure = Extract<ApiFailure, { kind: 'Validation' }>
export type ConflictFailure = Extract<ApiFailure, { kind: 'Conflict' }>

export interface ApiSuccess<T> {
  readonly ok: true
  readonly data: T
  readonly status: number
  readonly etag: string | null
  readonly requestId: string | null
}

export interface ApiRejection {
  readonly ok: false
  readonly failure: ApiFailure
}

export type ApiResult<T> = ApiSuccess<T> | ApiRejection

export function isFailure<T>(result: ApiResult<T>): result is ApiRejection {
  return !result.ok
}

export class ApiError extends Error {
  readonly failure: ApiFailure

  constructor(failure: ApiFailure) {
    super(failure.message)
    this.name = 'ApiError'
    this.failure = failure
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

export function unwrap<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data
  throw new ApiError(result.failure)
}
